import { useCallback, useEffect, useState } from 'react';
import './UsageBillingPanel.css';
import HelpDetails from './HelpDetails';

function monthNow() {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit' }).formatToParts(new Date());
  return `${parts.find((p) => p.type === 'year').value}-${parts.find((p) => p.type === 'month').value}`;
}
const money = (cents) => cents == null ? 'Da definire' : (cents / 100).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const duration = (seconds) => `${Math.floor(seconds / 60).toLocaleString('it-IT')} min ${seconds % 60} s`;
const date = (value) => new Date(value).toLocaleString('it-IT', { timeZone: 'Europe/Rome' });

export default function UsageBillingPanel({ session, restaurantId, compact = false, onOpen }) {
  const founder = Boolean(restaurantId);
  const [month, setMonth] = useState(monthNow);
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [agent, setAgent] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [refresh, setRefresh] = useState(0);
  const request = useCallback(async (action, body) => {
    const query = new URLSearchParams({ service: 'usage', action, month });
    if (founder) { query.set('scope', 'founder'); query.set('restaurantId', restaurantId); }
    const response = await fetch(`/api/billing?${query}`, {
      method: body ? 'POST' : 'GET',
      headers: { Authorization: `Bearer ${session.access_token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Consumi non disponibili.');
    if (action === 'usage' && (!result.usage || !result.estimate)) throw new Error('Riepilogo consumi non disponibile. Riprova dopo l’aggiornamento del servizio.');
    return result;
  }, [month, founder, restaurantId, session.access_token]);

  useEffect(() => {
    let active = true;
    setData(null); setError(''); setDrafts([]);
    Promise.all([request('usage'), founder ? request('drafts') : Promise.resolve({ drafts: [] })])
      .then(([summary, saved]) => { if (active) { setData(summary); setDrafts(saved.drafts); } })
      .catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [request, founder, refresh]);

  async function act(action, body = {}) {
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await request(action, body);
      setNotice(action === 'sync' ? result.complete
        ? 'Lettura completata fino all’orario indicato. Le chiamate in elaborazione richiedono un aggiornamento successivo.'
        : 'Prima parte registrata. Premi ancora “Sincronizza consumi” per continuare: il totale è ancora parziale.'
        : action === 'save-draft' ? 'Bozza salvata. Nessuna fattura emessa, nessun addebito.'
          : 'Agente associato solo per leggere i consumi. Telefonia invariata.');
      setRefresh((value) => value + 1);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  function downloadDraft(draft) {
    const url = URL.createObjectURL(new Blob([JSON.stringify(draft, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url; link.download = `ristodaily-bozza-${month}-${draft.id}.json`; link.click();
    URL.revokeObjectURL(url);
  }

  const remaining = data?.source?.enabled && data.scan?.complete && !data.usage.reviewCalls && data.usage.remainingSeconds != null
    ? duration(data.usage.remainingSeconds) : 'Da verificare';
  if (compact) return <section className="usage-billing usage-compact app-card glass-card" aria-label="Minuti del piano">
    <header><h3>Minuti del piano</h3>{onOpen && <button className="button button-ghost button-small" onClick={onOpen}>Piano e consumi</button>}</header>
    {error ? <p role="alert">Non riesco a caricare i minuti. Riprova in Piano e consumi.</p> : !data ? <p role="status">Caricamento minuti…</p> : <>
      <dl className="usage-metrics">
        <div className="usage-remaining"><dt>Minuti residui</dt><dd>{remaining}</dd></div>
        <div><dt>Minuti usati</dt><dd>{data.source?.enabled ? duration(data.usage.seconds) : 'Da verificare'}</dd></div>
        <div><dt>Minuti inclusi</dt><dd>{data.usage.includedMinutes == null ? 'Piano non assegnato' : `${data.usage.includedMinutes} min`}</dd></div>
      </dl>
      <p>{data.scan?.through ? `Aggiornato al ${date(data.scan.through)}. ` : ''}Il conteggio si aggiorna ogni notte, non in tempo reale.</p>
      {(!data.source?.enabled || !data.scan?.complete || data.usage.reviewCalls > 0 || data.automation?.needsAttention) && <p role="status">Dati da verificare: il residuo potrebbe non essere aggiornato.</p>}
    </>}
  </section>;
  return <section className="usage-billing app-card glass-card" aria-label="Consumi del piano">
    <header><div><h3>Consumi del mese</h3></div>
      <label>Mese di riferimento<input type="month" min="2026-01" max={monthNow()} value={month} disabled={busy} onChange={(e) => { setMonth(e.target.value); setNotice(''); }} /></label>
    </header>
    <p><strong>Addebiti disattivati.</strong> Nessun importo da pagare in questo riepilogo.</p>
    {error && <p className="form-alert error" role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {!data && !error && <p>Caricamento consumi…</p>}
    {data && <>
      {!data.source?.enabled && <p className="form-alert">Conteggio non collegato: l’assenza di dati non significa zero chiamate.</p>}
      {data.source?.enabled && <>
        {data.automation?.enabled && <p>
          Aggiornamento automatico ogni notte, non in tempo reale.
          {data.automation.needsAttention && <strong> Aggiornamento da verificare: i consumi mostrati potrebbero non essere aggiornati.</strong>}
        </p>}
        <p>{data.scan?.complete ? 'Lettura completata' : 'Dati parziali / da sincronizzare'}{data.scan?.through ? ` fino al ${date(data.scan.through)}` : ''}.</p>
        <dl className="usage-metrics">
          <div className="usage-remaining"><dt>Minuti residui</dt><dd>{remaining}</dd></div>
          <div><dt>Minuti registrati</dt><dd>{duration(data.usage.seconds)}</dd></div>
          <div><dt>Minuti inclusi</dt><dd>{data.usage.includedMinutes == null ? 'Piano non assegnato' : `${data.usage.includedMinutes.toLocaleString('it-IT')} min`}</dd></div>
          <div><dt>Extra registrati</dt><dd>{data.usage.extraSeconds == null ? '—' : duration(data.usage.extraSeconds)}</dd></div>
          <div><dt>Chiamate da verificare</dt><dd>{data.usage.reviewCalls}</dd></div>
        </dl>
        <p>SMS: {data.usage.smsEstimatedSegments} segmenti stimati
          {data.usage.smsIncludedSegments != null && ` · ${data.usage.smsIncludedSegments} inclusi · ${data.usage.smsExtraEstimatedSegments} extra stimati`}.</p>
        {data.usage.smsUnknownMessages > 0 && <p role="status">{data.usage.smsUnknownMessages} SMS con segmenti da verificare: il conteggio è incompleto.</p>}
        <HelpDetails title="Come leggere i consumi">
          <p>{data.usage.countedCalls} chiamate conteggiate · {data.usage.excludedCalls} escluse (anteprime o chiamate non in entrata).</p>
          {data.automation?.enabled && <p>{data.automation.lastSuccessAt ? `Ultimo aggiornamento automatico: ${date(data.automation.lastSuccessAt)}.` : 'Primo aggiornamento automatico in attesa.'}</p>}
          <p>Le chiamate in corso o elaborate in ritardo compaiono negli aggiornamenti successivi. Il residuo si riferisce alla lettura completata, non al tempo reale.</p>
          <p>SMS da riconciliare con l’operatore: questa stima non è il suo rendiconto. Tariffa SMS extra ancora da definire, nessun addebito.</p>
        </HelpDetails>
      </>}
      <details>
        <summary>Simulazione economica — non addebitabile</summary>
        <dl className="usage-metrics">
          <div><dt>Canone del piano attualmente assegnato</dt><dd>{money(data.estimate.monthlyCents)}</dd></div>
          <div><dt>Extra voce stimati a {money(data.estimate.extraMinuteCents)}/min</dt><dd>{data.source?.enabled ? money(data.estimate.extraVoiceCents) : 'Dati mancanti'}</dd></div>
          <div><dt>Subtotale piano + voce (non totale finale)</dt><dd>{data.source?.enabled ? money(data.estimate.subtotalVoiceCents) : 'Dati mancanti'}</dd></div>
          <div><dt>Totale da pagare</dt><dd>Non emesso</dd></div>
        </dl>
        <p>Misura in secondi effettivi, senza arrotondare ogni chiamata al minuto. Mese solare in Europe/Rome, chiamata attribuita al mese d’inizio.
          Simulazione con il piano attuale per un mese intero: non ricostruisce contratti precedenti, cambi piano o ratei.
          Regole commerciali, SMS extra, eventuali costi fissi e impostazione fiscale devono essere verificati prima di emettere qualsiasi fattura.</p>
      </details>
      {founder && <>
        {!data.source && <div className="usage-source">
          <label>ID agente ElevenLabs dedicato<input value={agent} onChange={(e) => setAgent(e.target.value)} placeholder="agent_…" disabled={busy} /></label>
          <button className="button button-ghost" disabled={busy || !/^agent_[A-Za-z0-9]+$/.test(agent)} onClick={() => act('register-source', { agentId: agent })}>Associa per il conteggio</button>
          <small>Solo l’agente di questo ristorante. L’associazione non modifica la telefonia, non abilita nuovi strumenti e non è riassegnabile da questo pannello.</small>
        </div>}
        <div className="usage-actions">
          <button className="button button-ghost" disabled={busy || !data.source?.enabled} onClick={() => act('sync')}>{busy ? 'Attendere…' : 'Sincronizza consumi'}</button>
          <button className="button button-ghost" disabled={busy} onClick={() => act('save-draft')}>Salva bozza senza addebiti</button>
          <button className="button button-ghost" disabled={busy} onClick={() => setRefresh((value) => value + 1)}>Aggiorna riepilogo</button>
        </div>
        {drafts.length > 0 && <ul className="usage-drafts">{drafts.map((draft) => <li key={draft.id}>
          <span>Bozza non fiscale · {date(draft.created_at)}</span>
          <button className="button button-ghost" onClick={() => downloadDraft(draft)}>Scarica dati della bozza</button>
        </li>)}</ul>}
      </>}
    </>}
  </section>;
}
